import { CloseOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  ACCOUNT_TYPE_SHORT,
  categoryFits,
  checkOperation,
  DEBT_DIRECTION_LABELS,
  describeError,
  dirhams,
  FINANCE_ERRORS,
  fullDate,
  IdTag,
  LOAN_CATEGORY,
  type OperationType,
  type OperationValues,
  PhotoField,
  parseOperationAmount,
  present,
  type ReceiptRule,
  receiptRequired,
  receiptRequiredMessage,
  TYPE_LABELS,
  today,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  Checkbox,
  ConfigProvider,
  Image,
  Input,
  type InputRef,
  Segmented,
  Select,
  Skeleton,
  Tag,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import {
  CreateOperationDocument,
  DeleteOperationDocument,
  type DetailOperation,
  financeContext,
  type Lists,
  lockReason,
  OperationDocument,
  UpdateOperationDocument,
  useCanWrite,
  useLists,
} from './data';
import { DebtForOperationDocument } from './movements-data';
import { DeskDrawer } from './panel';
import { CategoryPicker, CounterpartyPicker, ProjectPicker } from './pickers';
import { FlagButton, ReviewTrail } from './review';
import { KeyValues, Status, useEditKey, ViewBar } from './ui';

const EMPTY: OperationValues = {
  type: null,
  amount: '',
  date: today(),
  projectId: null,
  accountId: null,
  categoryId: null,
  counterpartyId: null,
  description: '',
  receiptPhotoId: null,
  needsReview: false,
  reviewNote: '',
  debtId: null,
};

const valuesOf = (op: DetailOperation): OperationValues => ({
  type: op.type,
  amount: String(Number(op.amount)),
  date: op.date,
  projectId: op.projectId,
  accountId: op.accountId,
  categoryId: op.categoryId,
  counterpartyId: op.counterpartyId,
  description: op.description ?? '',
  receiptPhotoId: op.receiptPhotoId,
  needsReview: op.needsReview,
  reviewNote: op.reviewNote ?? '',
  debtId: op.debtId,
});

/**
 * The side panel: one operation's full form (or a new one), its receipt
 * large, and the keys to go fast: Ctrl+S saves, Ctrl+Enter saves and opens
 * the next row (or a new blank one), Esc closes.
 */
export function OperationPanel({
  id,
  copyFrom,
  onClose,
  onOpen,
  onNew,
  nextId,
  draft,
  onSaved,
  onDirtyChange,
  debtId = null,
  presetType = null,
}: {
  /** A new operation of this loan (from the loan's panel). */
  debtId?: string | null;
  presetType?: OperationType | null;
  /** Changes made in the table and not saved yet: the form starts with them. */
  draft?: Partial<OperationValues>;
  /** After a save: the table drops this row's pending changes. */
  onSaved: (id: string) => void;
  /** Unsaved changes in the form (the screen asks before leaving them). */
  onDirtyChange: (dirty: boolean) => void;
  /** null: a new operation. */
  id: string | null;
  /** A new operation copied from this one («نسخ»). */
  copyFrom: string | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onNew: (copyFrom?: string) => void;
  nextId: string | null;
}) {
  const source = id ?? copyFrom;
  const [{ data, error }] = useQuery({
    query: OperationDocument,
    variables: { id: source ?? '' },
    pause: !source,
  });
  const [{ data: debtData }] = useQuery({
    query: DebtForOperationDocument,
    variables: { id: debtId ?? '' },
    pause: !debtId,
  });
  const [{ data: lists }] = useLists();
  const debt = debtData?.debt ?? null;

  let body: React.ReactNode;
  if ((debtId && !debtData) || (debtId && !lists)) {
    body = <Skeleton active paragraph={{ rows: 10 }} />;
  } else if (debt) {
    // Money of a loan: the loan, its lender, its project, «قروض».
    const loanCategory = present(lists?.operationCategories?.nodes).find(
      (c) => c.name === LOAN_CATEGORY,
    );
    body = (
      <>
        <Tag color="purple" className="desk-loan-tag">
          قرض: {DEBT_DIRECTION_LABELS[debt.direction]}
          {debt.counterparty ? ` · ${debt.counterparty.name}` : ''}
        </Tag>
        <Editor
          op={null}
          initial={{
            ...EMPTY,
            type: presetType,
            debtId: debt.id,
            projectId: debt.projectId,
            counterpartyId: debt.counterpartyId,
            categoryId: loanCategory?.id ?? null,
            description: debt.purpose ?? '',
          }}
          onClose={onClose}
          onOpen={onOpen}
          onNew={onNew}
          nextId={null}
          onSaved={onSaved}
          onDirtyChange={onDirtyChange}
        />
      </>
    );
  } else if (source && !data) {
    body = error ? (
      <Alert type="error" showIcon message="تعذّر تحميل العملية" />
    ) : (
      <Skeleton active paragraph={{ rows: 10 }} />
    );
  } else if (source && !data?.financialOperation) {
    body = <Alert type="warning" showIcon message="العملية غير موجودة" />;
  } else {
    const op = data?.financialOperation ?? null;
    body = (
      <Editor
        op={id ? op : null}
        initial={
          id && op
            ? valuesOf(op)
            : op
              ? // A copy: same content, today's date, no receipt nor review.
                {
                  ...valuesOf(op),
                  date: today(),
                  receiptPhotoId: null,
                  needsReview: false,
                  reviewNote: '',
                  debtId: null,
                }
              : EMPTY
        }
        onClose={onClose}
        onOpen={onOpen}
        onNew={onNew}
        nextId={nextId}
        draft={id ? draft : undefined}
        onSaved={onSaved}
        onDirtyChange={onDirtyChange}
      />
    );
  }

  return (
    <DeskDrawer label="العملية">
      <div className="desk-panel-head">
        <strong>
          {id
            ? data?.financialOperation
              ? `${TYPE_LABELS[data.financialOperation.type]}: ${dirhams(data.financialOperation.amount)}`
              : 'العملية'
            : copyFrom
              ? 'عملية جديدة (نسخة)'
              : debt
                ? presetType === 'credit'
                  ? debt.direction === 'borrowed'
                    ? 'تسجيل المبلغ المستلم'
                    : 'تسجيل استرجاع'
                  : debt.direction === 'borrowed'
                    ? 'تسجيل سداد'
                    : 'تسجيل المبلغ المُسلَّم'
                : 'عملية جديدة'}
        </strong>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="إغلاق (Esc)"
          title="إغلاق (Esc)"
        />
      </div>
      <div className="desk-panel-body">
        {id && data?.financialOperation && (
          <div className="desk-panel-id">
            <IdTag id={id} link />
          </div>
        )}
        {body}
      </div>
    </DeskDrawer>
  );
}

type SaveMode = 'stay' | 'next' | 'another';

function Editor({
  op,
  initial,
  onClose,
  onOpen,
  onNew,
  nextId,
  draft,
  onSaved,
  onDirtyChange,
}: {
  op: DetailOperation | null;
  initial: OperationValues;
  draft?: Partial<OperationValues>;
  onSaved: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  onClose: () => void;
  onOpen: (id: string) => void;
  onNew: (copyFrom?: string) => void;
  nextId: string | null;
}) {
  const { message, modal } = App.useApp();
  const canWrite = useCanWrite();
  const [{ data: lists }] = useLists();
  // `start`: what is saved; `values`: the form, with the table's pending
  // changes for this row if any.
  const [start, setStart] = useState(initial);
  // Read first (doc 61); a new operation, or one with table changes, opens
  // in its form.
  const [editing, setEditing] = useState(!op || !!draft);
  const [values, setValues] = useState<OperationValues>(() => ({
    ...initial,
    ...draft,
  }));
  const dirty = JSON.stringify(values) !== JSON.stringify(start);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  // A new key empties the receipt field after «حفظ وإضافة أخرى».
  const [round, setRound] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const amountRef = useRef<InputRef>(null);
  const [{ fetching: creating }, create] = useMutation(CreateOperationDocument);
  const [{ fetching: updating }, update] = useMutation(UpdateOperationDocument);
  const [, remove] = useMutation(DeleteOperationDocument);
  const saving = creating || updating;

  const set = (patch: Partial<OperationValues>) =>
    setValues((v) => ({ ...v, ...patch }));
  const categories = present(lists?.operationCategories?.nodes);
  const rule: ReceiptRule = {
    minAmount: lists?.currentSettings?.receiptPhotoMinAmount ?? null,
    saved: op ? { type: op.type, amount: op.amount, source: op.source } : null,
  };
  const { problems, operation } = checkOperation(values, rule, { categories });
  const photoRequired = receiptRequired(values, rule);
  const amount = parseOperationAmount(values.amount);
  const locked = op
    ? lockReason(op, canWrite)
    : canWrite
      ? null
      : 'للاطلاع فقط.';

  useEffect(() => {
    if (!op) amountRef.current?.focus();
  }, [op]);
  useEditKey(!editing && !locked, () => setEditing(true));

  async function save(mode: SaveMode) {
    if (!editing || locked || saving || uploading) return;
    setSubmitted(true);
    setProblem(null);
    if (!operation) return;
    if (op) {
      const result = await update(
        { id: op.id, patch: operation },
        financeContext,
      );
      if (result.error) {
        setProblem(
          describeError(result.error, 'تعذّر حفظ التعديلات.', FINANCE_ERRORS),
        );
        return;
      }
      message.success(
        op.needsReview && !operation.needsReview
          ? 'حُفظت وأُزيلت علامة المراجعة'
          : 'حُفظت التعديلات',
      );
      setStart(values);
      setSubmitted(false);
      onSaved(op.id);
      if (mode === 'stay') setEditing(false);
      if (mode === 'next' && nextId) onOpen(nextId);
      return;
    }
    const result = await create({ operation }, financeContext);
    const createdId =
      result.data?.createFinancialOperation?.financialOperation?.id;
    if (result.error || !createdId) {
      setProblem(
        describeError(result.error, 'تعذّر حفظ العملية.', FINANCE_ERRORS),
      );
      return;
    }
    message.success(
      `حُفظ ${TYPE_LABELS[operation.type]}: ${dirhams(operation.amount)}`,
    );
    if (mode === 'another') {
      // Same day, account and project: entering a pile of receipts.
      const next: OperationValues = {
        ...EMPTY,
        type: values.type,
        date: values.date,
        accountId: values.accountId,
        projectId: values.projectId,
      };
      setStart(next);
      setValues(next);
      setSubmitted(false);
      setRound((r) => r + 1);
      onSaved(createdId);
      amountRef.current?.focus();
    } else {
      setStart(values);
      onSaved(createdId);
      onOpen(createdId);
    }
  }

  // Keys work anywhere on the page while the panel is open.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && e.code === 'KeyS') {
        e.preventDefault();
        saveRef.current('stay');
      } else if (ctrl && e.key === 'Enter') {
        e.preventDefault();
        saveRef.current(op ? 'next' : 'another');
      } else if (e.key === 'Escape') {
        // An open list or dialog closes first.
        if (
          document.querySelector(
            '.ant-select-dropdown:not(.ant-select-dropdown-hidden), .ant-modal-wrap, .ant-image-preview-wrap',
          )
        ) {
          return;
        }
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [op, onClose]);

  async function confirmDelete() {
    if (!op) return;
    const confirmed = await modal.confirm({
      title: 'حذف العملية',
      content: `${TYPE_LABELS[op.type]} ${dirhams(op.amount)} بتاريخ ${fullDate(op.date)}. لا يمكن التراجع.`,
      okText: 'نعم، احذفها',
      okButtonProps: { danger: true },
      cancelText: 'رجوع',
    });
    if (!confirmed) return;
    const result = await remove({ id: op.id }, financeContext);
    if (result.error) {
      message.error(
        describeError(result.error, 'تعذّر حذف العملية.', FINANCE_ERRORS),
      );
      return;
    }
    message.success('حُذفت العملية');
    onClose();
  }

  const chooseType = (type: OperationType) => {
    const category = categories.find((c) => c.id === values.categoryId);
    const fits = !category || categoryFits(category.appliesTo, type);
    set({ type, categoryId: fits ? values.categoryId : null });
  };

  return (
    <>
      {locked && <Alert type="info" showIcon message={locked} />}
      {op && !canWrite && (
        <div className="desk-flag">
          <FlagButton kind="operation" id={op.id} />
        </div>
      )}
      {op && (
        <div className="desk-panel-tags">
          {op.source === 'ledger_import' && <Tag>مستورد من الدفاتر</Tag>}
          {op.source === 'bank_statement' && (
            <Tag color="blue">من كشف بنكي</Tag>
          )}
          {op.debt && (
            <Tag color="purple">
              قرض: {DEBT_DIRECTION_LABELS[op.debt.direction]}
              {op.debt.counterparty ? ` · ${op.debt.counterparty.name}` : ''}
            </Tag>
          )}
        </div>
      )}
      {op?.receiptPhoto && <ReceiptViewer photo={op.receiptPhoto} />}

      {!editing && op ? (
        <OperationView
          values={start}
          op={op}
          lists={lists}
          onEdit={locked ? undefined : () => setEditing(true)}
          onCopy={() => onNew(op.id)}
          onDelete={locked ? undefined : confirmDelete}
        />
      ) : (
        <ConfigProvider componentDisabled={!!locked}>
          <form
            className="desk-form"
            onSubmit={(e) => {
              e.preventDefault();
              save('stay');
            }}
          >
            <Segmented<OperationType>
              block
              className={`desk-type is-${values.type ?? 'none'}`}
              value={values.type ?? undefined}
              onChange={chooseType}
              options={[
                { value: 'credit', label: '+ مدخول' },
                { value: 'debit', label: '− مصروف' },
              ]}
            />

            <div className="desk-fields">
              <label htmlFor="desk-amount">المبلغ (درهم)</label>
              <label htmlFor="desk-date">التاريخ</label>
              <span>
                <Input
                  id="desk-amount"
                  ref={amountRef}
                  inputMode="decimal"
                  className="ltr-num desk-amount-input"
                  value={values.amount}
                  onChange={(e) => set({ amount: e.target.value })}
                />
                {amount && (
                  <span className="field-hint">{dirhams(amount)}</span>
                )}
              </span>
              <Input
                id="desk-date"
                type="date"
                className="ltr-num"
                value={values.date}
                onChange={(e) => set({ date: e.target.value })}
              />

              <label htmlFor="desk-account">الحساب</label>
              <label htmlFor="desk-project">المشروع</label>
              <Select
                id="desk-account"
                showSearch
                optionFilterProp="label"
                placeholder="الصندوق أو البنك"
                value={values.accountId ?? undefined}
                onChange={(accountId) => set({ accountId })}
                options={present(lists?.financialAccounts?.nodes).map((a) => ({
                  value: a.id,
                  label: `${a.name} · ${ACCOUNT_TYPE_SHORT[a.type]}`,
                }))}
              />
              <ProjectPicker
                id="desk-project"
                placeholder="المشروع"
                keep={start.projectId}
                value={values.projectId}
                onChange={(projectId) => set({ projectId })}
              />

              <label htmlFor="desk-category">الصنف</label>
              <label htmlFor="desk-counterparty">الجهة</label>
              <CategoryPicker
                id="desk-category"
                type={values.type}
                placeholder={values.type ? 'الصنف' : 'اختر النوع أولاً'}
                value={values.categoryId}
                onChange={(categoryId) => set({ categoryId })}
              />
              <CounterpartyPicker
                id="desk-counterparty"
                type={values.type}
                value={values.counterpartyId}
                onChange={(counterpartyId) => set({ counterpartyId })}
                placeholder="مورد، عامل، متبرع…"
              />
            </div>

            <label htmlFor="desk-description">البيان</label>
            <Input.TextArea
              id="desk-description"
              autoSize={{ minRows: 2, maxRows: 5 }}
              value={values.description}
              onChange={(e) => set({ description: e.target.value })}
            />

            {!locked && (
              <PhotoField
                key={round}
                kind="RECEIPT"
                label={op?.receiptPhoto ? 'تغيير الوصل' : 'صورة الوصل'}
                required={photoRequired && !values.receiptPhotoId}
                hint={
                  photoRequired && !values.receiptPhotoId
                    ? receiptRequiredMessage(rule.minAmount)
                    : 'اسحب الملف هنا، أو الصق صورة (Ctrl+V)، أو اختر من الحاسوب. صورة أو PDF.'
                }
                value={
                  values.receiptPhotoId !== start.receiptPhotoId
                    ? values.receiptPhotoId
                    : null
                }
                onChange={(receiptPhotoId) =>
                  set({
                    receiptPhotoId: receiptPhotoId ?? start.receiptPhotoId,
                  })
                }
                onBusyChange={setUploading}
                pasteFromClipboard
              />
            )}

            <div className={`desk-review${values.needsReview ? ' is-on' : ''}`}>
              <Checkbox
                checked={values.needsReview}
                onChange={(e) => set({ needsReview: e.target.checked })}
              >
                تحتاج مراجعة
              </Checkbox>
              {values.needsReview && (
                <Input
                  placeholder="ما الذي يجب التحقق منه؟"
                  value={values.reviewNote}
                  onChange={(e) => set({ reviewNote: e.target.value })}
                />
              )}
            </div>

            {submitted && problems.length > 0 && (
              <ul className="field-error desk-problems">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}
            {problem && <Alert type="error" showIcon message={problem} />}

            {!locked && (
              <div className="desk-actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  disabled={uploading}
                  title="Ctrl+S"
                >
                  حفظ
                </Button>
                {op ? (
                  <Button
                    onClick={() => save('next')}
                    disabled={uploading || !nextId}
                    title="Ctrl+Enter"
                  >
                    حفظ والتالي
                  </Button>
                ) : (
                  <Button
                    onClick={() => save('another')}
                    disabled={uploading}
                    title="Ctrl+Enter"
                  >
                    حفظ وإضافة أخرى
                  </Button>
                )}
                <span className="desk-actions-gap" />
                {op && (
                  <Button
                    onClick={() => {
                      setValues(start);
                      setProblem(null);
                      setSubmitted(false);
                      setEditing(false);
                    }}
                  >
                    إلغاء
                  </Button>
                )}
              </div>
            )}
          </form>
        </ConfigProvider>
      )}

      {op && (
        <div className="field-hint desk-meta">
          أُدخلت {fullDate(op.createdAt)}
          {op.updatedAt !== op.createdAt && ` · عُدِّلت ${fullDate(op.updatedAt)}`}
        </div>
      )}
      {op && <ReviewTrail kind="operation" id={op.id} />}
    </>
  );
}

/** The saved receipt, large: an image to zoom and rotate, or the PDF itself. */
function ReceiptViewer({
  photo,
}: {
  photo: { url: string; contentType: string; uploadedBy: string | null };
}) {
  if (photo.contentType === 'application/pdf') {
    return (
      <div className="desk-receipt">
        <iframe src={photo.url} title="الوصل (PDF)" />
        <a href={photo.url} target="_blank" rel="noreferrer">
          فتح الملف في نافذة جديدة
        </a>
      </div>
    );
  }
  return (
    <div className="desk-receipt">
      <Image src={photo.url} alt="الوصل" preview={{ mask: 'تكبير وتدوير' }} />
    </div>
  );
}

/** An operation read (doc 61): its facts, then «تعديل (E)», «نسخ», «حذف». */
function OperationView({
  values: v,
  op,
  lists,
  onEdit,
  onCopy,
  onDelete,
}: {
  values: OperationValues;
  op: DetailOperation;
  lists: Lists | undefined;
  onEdit?: () => void;
  onCopy: () => void;
  onDelete?: () => void;
}) {
  const name = (
    pool: ({ id: string; name: string } | null)[] | undefined,
    id: string | null,
  ) => (id ? present(pool).find((x) => x.id === id)?.name : null);
  return (
    <div className="ui-stack">
      <KeyValues
        columns={2}
        items={[
          {
            label: 'المبلغ',
            value: v.type && (
              <strong className={`desk-amount is-${v.type}`}>
                {v.type === 'credit' ? '+' : '−'} {dirhams(v.amount)}
              </strong>
            ),
          },
          { label: 'النوع', value: v.type && TYPE_LABELS[v.type] },
          { label: 'التاريخ', value: fullDate(v.date) },
          {
            label: 'الحساب',
            value: name(lists?.financialAccounts?.nodes, v.accountId),
          },
          {
            label: 'المشروع',
            value:
              name(lists?.projects?.nodes, v.projectId) ?? op.project?.name,
          },
          {
            label: 'الصنف',
            value: name(lists?.operationCategories?.nodes, v.categoryId),
          },
          {
            label: 'الجهة',
            value: name(lists?.counterparties?.nodes, v.counterpartyId),
          },
          {
            label: 'الوصل',
            value: op.receiptPhoto ? (
              <Status tone="success">مرفق</Status>
            ) : (
              <Status tone="stopped">بلا وصل</Status>
            ),
          },
          { label: 'البيان', value: v.description, wide: true },
          {
            label: 'المراجعة',
            wide: true,
            value: v.needsReview ? (
              <Status tone="warning">
                تحتاج مراجعة
                {v.reviewNote && (
                  <span className="ui-review-note">: {v.reviewNote}</span>
                )}
              </Status>
            ) : (
              <Status tone="success">لا تحتاج مراجعة</Status>
            ),
          },
        ]}
      />
      <ViewBar onEdit={onEdit}>
        <Button icon={<CopyOutlined />} onClick={onCopy}>
          نسخ
        </Button>
        {onDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
            حذف
          </Button>
        )}
      </ViewBar>
    </div>
  );
}
